/**
 * HeatingCostErrorBoundary — Error boundary for HeatingCostSection
 *
 * Catches runtime errors in the heating cost comparison UI
 * and displays a fallback message instead of crashing the entire results view.
 */

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Alert, Text } from '@mantine/core';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class HeatingCostErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log for debugging — will be removed for production via feature flag
    if (import.meta.env.DEV) {
      console.error('[HeatingCostSection] Error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert color="orange" variant="light" title="Koszty ogrzewania niedostępne">
          <Text size="sm">
            Wystąpił błąd podczas obliczania kosztów ogrzewania.
            Pozostałe wyniki symulacji są poprawne.
          </Text>
        </Alert>
      );
    }

    return this.props.children;
  }
}
